import * as React from "react";
import {useCallback, useMemo} from "react";
import {IResearchPagination} from "../../model/ResearchPageState.model";
import {getTranslation} from "../../utils/translationUtil";

export interface IResearchPaginationProps {
    pagination: IResearchPagination;
    totalItems: number;
    onPaginationChange: (pagination: IResearchPagination) => void;
}

const PAGE_SIZE_OPTIONS = [25, 50, 100, 250];
const MAX_VISIBLE_PAGES = 5;

function getPageWindow(currentPage: number, totalPages: number): (number | "ellipsis")[] {
    if (totalPages <= MAX_VISIBLE_PAGES) {
        return Array.from({length: totalPages}, (_, i) => i);
    }

    const pages: (number | "ellipsis")[] = [];
    const half = Math.floor(MAX_VISIBLE_PAGES / 2);
    let start = Math.max(1, currentPage - half + 1);
    const end = Math.min(totalPages - 2, start + MAX_VISIBLE_PAGES - 3);
    start = Math.max(1, end - MAX_VISIBLE_PAGES + 3);

    pages.push(0);
    if (start > 1) pages.push("ellipsis");
    for (let i = start; i <= end; i++) pages.push(i);
    if (end < totalPages - 2) pages.push("ellipsis");
    pages.push(totalPages - 1);

    return pages;
}

export const ResearchPagination: React.FunctionComponent<IResearchPaginationProps> = (props) => {
    const {pagination, totalItems, onPaginationChange} = props;
    const totalPages = Math.max(1, Math.ceil(totalItems / pagination.pageSize));
    const currentPage = pagination.pageIndex;

    const pageWindow = useMemo(() => getPageWindow(currentPage, totalPages), [currentPage, totalPages]);

    const startItem = totalItems === 0 ? 0 : currentPage * pagination.pageSize + 1;
    const endItem = Math.min((currentPage + 1) * pagination.pageSize, totalItems);

    const goToPage = useCallback((page: number) => {
        onPaginationChange({...pagination, pageIndex: page});
    }, [pagination, onPaginationChange]);

    const handlePageSizeChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
        onPaginationChange({pageIndex: 0, pageSize: parseInt(e.target.value, 10)});
    }, [onPaginationChange]);

    const firstPageLabel = getTranslation("research-page.first-page", "First page");
    const prevPageLabel = getTranslation("research-page.previous-page", "Previous page");
    const nextPageLabel = getTranslation("research-page.next-page", "Next page");
    const lastPageLabel = getTranslation("research-page.last-page", "Last page");

    return (
        <nav className="research-pagination" role="navigation" aria-label={getTranslation("research-page.pagination", "Table pagination")}>

            {/* Left: row-count summary */}
            <div className="research-pagination__info" aria-live="polite">
                {getTranslation("research-page.showing", "Showing")}{" "}
                <strong>{startItem}&ndash;{endItem}</strong>{" "}
                {getTranslation("research-page.of", "of")}{" "}
                <strong>{totalItems}</strong>{" "}
                {getTranslation("research-page.strategies", "strategies")}
            </div>

            {/* Centre: page navigation */}
            <div className="research-pagination__controls">
                <button
                    type="button"
                    className="research-pagination__button"
                    onClick={() => goToPage(0)}
                    disabled={currentPage === 0}
                    aria-label={firstPageLabel}
                    title={firstPageLabel}
                >
                    <i className="fa-solid fa-angles-left" aria-hidden="true" />
                </button>
                <button
                    type="button"
                    className="research-pagination__button"
                    onClick={() => goToPage(currentPage - 1)}
                    disabled={currentPage === 0}
                    aria-label={prevPageLabel}
                    title={prevPageLabel}
                >
                    <i className="fa-solid fa-angle-left" aria-hidden="true" />
                </button>

                {pageWindow.map((item, idx) => {
                    if (item === "ellipsis") {
                        return <span key={`ellipsis-${idx}`} className="research-pagination__ellipsis">&hellip;</span>;
                    }
                    const pageLabel = getTranslation("research-page.page-number", "Page {n}", {n: item + 1});
                    return (
                        <button
                            key={item}
                            type="button"
                            className={`research-pagination__button${item === currentPage ? " research-pagination__button--active" : ""}`}
                            onClick={() => goToPage(item)}
                            aria-label={pageLabel}
                            aria-current={item === currentPage ? "page" : undefined}
                            title={pageLabel}
                        >
                            {item + 1}
                        </button>
                    );
                })}

                <button
                    type="button"
                    className="research-pagination__button"
                    onClick={() => goToPage(currentPage + 1)}
                    disabled={currentPage >= totalPages - 1}
                    aria-label={nextPageLabel}
                    title={nextPageLabel}
                >
                    <i className="fa-solid fa-angle-right" aria-hidden="true" />
                </button>
                <button
                    type="button"
                    className="research-pagination__button"
                    onClick={() => goToPage(totalPages - 1)}
                    disabled={currentPage >= totalPages - 1}
                    aria-label={lastPageLabel}
                    title={lastPageLabel}
                >
                    <i className="fa-solid fa-angles-right" aria-hidden="true" />
                </button>
            </div>

            {/* Right: per-page selector */}
            <div className="research-pagination__page-size">
                <label htmlFor="research-page-size" className="research-pagination__page-size-label">
                    {getTranslation("research-page.per-page", "Per page")}:
                </label>
                <select
                    id="research-page-size"
                    className="research-pagination__page-size-select"
                    value={pagination.pageSize}
                    onChange={handlePageSizeChange}
                >
                    {PAGE_SIZE_OPTIONS.map((size) => (
                        <option key={size} value={size}>{size}</option>
                    ))}
                </select>
            </div>
        </nav>
    );
};
